<template>
  <div class="admin-news">
    <div class="news-header">
      <div class="header-left">
        <h2>Haber Yönetimi</h2>
        <p>Sitedeki haberleri buradan yönetebilirsiniz</p>
      </div>
      <div class="header-actions">
        <button @click="showAddModal = true" class="btn btn-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          Yeni Haber Ekle
        </button>
        <button @click="refreshNews" class="btn btn-secondary" :disabled="isLoading">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
          Yenile
        </button>
      </div>
    </div>

    <div class="news-filters">
      <div class="filter-group">
        <label>Durum:</label>
        <select v-model="filters.published" @change="loadNews">
          <option value="all">Tümü</option>
          <option value="published">Yayınlanan</option>
          <option value="draft">Taslak</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Arama:</label>
        <input 
          v-model="filters.search" 
          @input="debouncedSearch"
          type="text" 
          placeholder="Haber başlığı veya içeriği..."
          class="search-input"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Haberler yükleniyor...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="news.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
      </svg>
      <h3>{{ getEmptyStateMessage() }}</h3>
      <p>Henüz haber bulunmuyor</p>
      <button @click="showAddModal = true" class="btn btn-primary">
        İlk Haberi Ekle
      </button>
    </div>

    <!-- News List -->
    <div v-else class="news-list">
      <div class="news-grid">
        <div 
          v-for="article in news" 
          :key="article.id"
          class="news-card"
          :class="{ 'draft': !article.is_published }"
        >
          <div class="news-image">
            <img 
              v-if="article.image_url"
              :src="article.image_url"
              :alt="article.title_tr"
              @error="handleImageError"
            />
            <div v-else class="no-image">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
          </div>
          
          <div class="news-content">
            <div class="news-status">
              <span 
                class="status-badge" 
                :class="article.is_published ? 'published' : 'draft'"
              >
                {{ article.is_published ? 'Yayında' : 'Taslak' }}
              </span>
              <span class="news-date">{{ formatDate(article.publish_date) }}</span>
            </div>
            
            <h3 class="news-title">{{ article.title_tr }}</h3>
            <p class="news-excerpt">{{ article.excerpt_tr }}</p>
            
            <div class="news-actions">
              <button @click="editNews(article)" class="action-btn edit">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                Düzenle
              </button>
              <button 
                @click="togglePublished(article)" 
                class="action-btn toggle"
                :class="article.is_published ? 'unpublish' : 'publish'"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path v-if="article.is_published" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  <path v-else d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                </svg>
                {{ article.is_published ? 'Yayından Kaldır' : 'Yayınla' }}
              </button>
              <button @click="deleteNews(article)" class="action-btn delete">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Sil
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button 
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="page-btn"
        >
          ‹ Önceki
        </button>
        <div class="page-numbers">
          <button 
            v-for="page in visiblePages"
            :key="page"
            @click="changePage(page)"
            :class="{ active: page === currentPage }"
            class="page-btn"
          >
            {{ page }}
          </button>
        </div>
        <button 
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="page-btn"
        >
          Sonraki ›
        </button>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ showAddModal ? 'Yeni Haber Ekle' : 'Haber Düzenle' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="saveNews">
            <div class="form-group">
              <label>Başlık (TR) *</label>
              <input v-model="newsForm.title_tr" type="text" required />
            </div>
            
            <div class="form-group">
              <label>Başlık (EN) *</label>
              <input v-model="newsForm.title_en" type="text" required />
            </div>
            
            <div class="form-group">
              <label>Özet (TR) *</label>
              <textarea v-model="newsForm.excerpt_tr" rows="3" required></textarea>
            </div>
            
            <div class="form-group">
              <label>Özet (EN) *</label>
              <textarea v-model="newsForm.excerpt_en" rows="3" required></textarea>
            </div>
            
            <div class="form-group">
              <label>İçerik (TR) *</label>
              <textarea v-model="newsForm.content_tr" rows="6" required></textarea>
            </div>
            
            <div class="form-group">
              <label>İçerik (EN) *</label>
              <textarea v-model="newsForm.content_en" rows="6" required></textarea>
            </div>
            
            <div class="form-group">
              <label>Görsel URL</label>
              <input v-model="newsForm.image_url" type="url" placeholder="https://..." />
            </div>
            
            <div class="form-group">
              <label>Yayın Tarihi</label>
              <input v-model="newsForm.publish_date" type="date" />
            </div>
            
            <div class="form-group">
              <label>
                <input v-model="newsForm.is_published" type="checkbox" />
                Yayınla
              </label>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeModal" class="btn btn-secondary">
                İptal
              </button>
              <button type="submit" class="btn btn-primary" :disabled="isSaving">
                {{ isSaving ? 'Kaydediliyor...' : 'Kaydet' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { adminApi } from '@/services/adminApi'

const emit = defineEmits(['statsUpdated'])

// State
const news = ref([])
const isLoading = ref(true)
const isSaving = ref(false)
const showAddModal = ref(false)
const showEditModal = ref(false)
const currentPage = ref(1)
const totalCount = ref(0)
const itemsPerPage = ref(12)

// Filters
const filters = reactive({
  published: 'all',
  search: ''
})

// Form
const newsForm = reactive({
  title_tr: '',
  title_en: '',
  excerpt_tr: '',
  excerpt_en: '',
  content_tr: '',
  content_en: '',
  image_url: '',
  publish_date: new Date().toISOString().split('T')[0],
  is_published: false
})

const editingId = ref(null)

// Computed
const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage.value))

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, currentPage.value + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Methods
const loadNews = async () => {
  try {
    isLoading.value = true
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      published: filters.published === 'all' ? undefined : filters.published === 'published'
    }
    
    if (filters.search) {
      params.search = filters.search
    }
    
    const response = await adminApi.getNews(params)
    if (response.success) {
      news.value = response.data.news || []
      totalCount.value = response.data.pagination?.total || 0
      
      // Emit stats update
      emit('statsUpdated', {
        total: totalCount.value,
        published: news.value.filter(n => n.is_published).length
      })
    }
  } catch (error) {
    console.error('Haberler yüklenemedi:', error)
  } finally {
    isLoading.value = false
  }
}

const refreshNews = () => {
  currentPage.value = 1
  loadNews()
}

// Debounced search
let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadNews()
  }, 500)
}

const changePage = (page) => {
  currentPage.value = page
  loadNews()
}

const getEmptyStateMessage = () => {
  if (filters.search) return `"${filters.search}" için sonuç bulunamadı`
  if (filters.published === 'published') return 'Yayınlanan haber bulunmuyor'
  if (filters.published === 'draft') return 'Taslak haber bulunmuyor'
  return 'Henüz haber eklenmemiş'
}

const editNews = (article) => {
  editingId.value = article.id
  Object.assign(newsForm, {
    title_tr: article.title_tr,
    title_en: article.title_en,
    excerpt_tr: article.excerpt_tr,
    excerpt_en: article.excerpt_en,
    content_tr: article.content_tr,
    content_en: article.content_en,
    image_url: article.image_url || '',
    publish_date: article.publish_date,
    is_published: article.is_published
  })
  showEditModal.value = true
}

const togglePublished = async (article) => {
  try {
    const response = await adminApi.updateNews(article.id, {
      is_published: !article.is_published
    })
    if (response.success) {
      article.is_published = !article.is_published
    } else {
      alert('Yayın durumu güncellenemedi: ' + (response.error || 'Bilinmeyen hata'))
    }
  } catch (error) {
    console.error('Yayın durumu güncellenemedi:', error)
    alert('Yayın durumu güncellenemedi: ' + error.message)
  }
}

const deleteNews = async (article) => {
  if (!confirm(`"${article.title_tr}" başlıklı haberi silmek istediğinizden emin misiniz?`)) {
    return
  }

  try {
    const response = await adminApi.deleteNews(article.id)
    if (response.success) {
      news.value = news.value.filter(n => n.id !== article.id)
      totalCount.value--
    } else {
      alert('Haber silinemedi: ' + (response.error || 'Bilinmeyen hata'))
    }
  } catch (error) {
    console.error('Haber silinemedi:', error)
    alert('Haber silinemedi: ' + error.message)
  }
}

const saveNews = async () => {
  try {
    isSaving.value = true
    let response
    
    if (showEditModal.value) {
      response = await adminApi.updateNews(editingId.value, newsForm)
    } else {
      response = await adminApi.createNews(newsForm)
    }
    
    if (response.success) {
      closeModal()
      loadNews()
    } else {
      alert('Haber kaydedilemedi: ' + (response.error || 'Bilinmeyen hata'))
    }
  } catch (error) {
    console.error('Haber kaydedilemedi:', error)
    alert('Haber kaydedilemedi: ' + error.message)
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showAddModal.value = false
  showEditModal.value = false
  editingId.value = null
  Object.assign(newsForm, {
    title_tr: '',
    title_en: '',
    excerpt_tr: '',
    excerpt_en: '',
    content_tr: '',
    content_en: '',
    image_url: '',
    publish_date: new Date().toISOString().split('T')[0],
    is_published: false
  })
}

const handleImageError = (event) => {
  event.target.style.display = 'none'
  event.target.nextElementSibling.style.display = 'flex'
}

const formatDate = (dateString) => {
  if (!dateString) return 'Tarih belirtilmemiş'
  const date = new Date(dateString)
  return date.toLocaleDateString('tr-TR')
}

onMounted(() => {
  loadNews()
})
</script>

<style scoped>
.admin-news {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.news-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.header-left h2 {
  margin: 0 0 4px 0;
  color: #1a202c;
  font-size: 28px;
  font-weight: 600;
}

.header-left p {
  margin: 0;
  color: #718096;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.news-filters {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-group select,
.search-input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.filter-group select:focus,
.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-input {
  min-width: 250px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state h3 {
  margin: 16px 0 8px;
  color: #4a5568;
}

.empty-state p {
  color: #718096;
  margin-bottom: 20px;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.news-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.news-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.news-card.draft {
  opacity: 0.7;
  border-color: #fbbf24;
}

.news-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
  color: #cbd5e0;
}

.news-content {
  padding: 20px;
}

.news-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.published {
  background: #c6f6d5;
  color: #22543d;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.news-date {
  font-size: 12px;
  color: #718096;
}

.news-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
}

.news-excerpt {
  margin: 0 0 16px 0;
  color: #718096;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  background: #e2e8f0;
  color: #4a5568;
}

.action-btn.edit:hover {
  background: #cbd5e0;
}

.action-btn.publish {
  background: #c6f6d5;
  color: #22543d;
}

.action-btn.unpublish {
  background: #fef3c7;
  color: #92400e;
}

.action-btn.delete {
  background: #fed7d7;
  color: #c53030;
}

.action-btn.delete:hover {
  background: #feb2b2;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
}

.page-btn {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover {
  background: #f7fafc;
}

.page-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #718096;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #4a5568;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input[type="checkbox"] {
  width: auto;
  margin-right: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 768px) {
  .admin-news {
    padding: 16px;
  }
  
  .news-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: flex-start;
  }
  
  .news-filters {
    flex-direction: column;
  }
  
  .search-input {
    min-width: 100%;
  }
  
  .news-grid {
    grid-template-columns: 1fr;
  }
  
  .modal {
    width: 95%;
    margin: 20px;
  }
}
</style>